import { useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { Fragment, useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, View, XStack, YStack } from 'tamagui';

import PSIcon from '@/components/ui/PSIcon';
import PSInput from '@/components/ui/PSInput';
import PSText from '@/components/ui/PSText';
import {
  COMPONENT_CATALOG,
  PAGE_CATALOG,
  type CatalogEntry,
} from '@/src/lib/componentCatalog';

/**
 * ── /components ──────────────────────────────────────────────────────────
 *
 * The dev gallery's front door — a searchable list of every previewed
 * page and component, in place of the single ever-growing page this used to be. Each
 * row comes from `PAGE_CATALOG` / `COMPONENT_CATALOG` (`src/lib/componentCatalog.ts`) and IS a
 * route; this screen only filters and lists them, it holds no preview code of
 * its own. A new component's preview gets its own route + a catalog entry —
 * never a block appended here.
 * ─────────────────────────────────────────────────────────────────────────
 */

const matchesQuery = (entry: CatalogEntry, query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    entry.title.toLowerCase().includes(q) ||
    entry.blurb.toLowerCase().includes(q) ||
    entry.keywords.some((keyword) => keyword.toLowerCase().includes(q))
  );
};

export default function ComponentsCatalog() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const sections = [
    { title: 'Pages', entries: PAGE_CATALOG },
    { title: 'Components', entries: COMPONENT_CATALOG },
  ]
    .map((section) => ({
      ...section,
      entries: section.entries.filter((entry) => matchesQuery(entry, query)),
    }))
    .filter((section) => section.entries.length > 0);

  return (
    <>
      <Head>
        <title>Components · Putting Scene</title>
      </Head>
      {/* Plain RN ScrollView, not PageScrollView: PageScrollView assumes a
          transparent header and adds a matching top inset, but this group's
          header (app/components/_layout.tsx) is opaque. Migrating the
          gallery's scroll frame onto PageScrollView is a separate concern
          from this commit, so we match the frame all.tsx already uses
          instead. */}
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack gap="$xl" p="$lg">
          <YStack gap="$sm">
            <PSText.H1 color="$textDark">Components</PSText.H1>
            <PSText.Body4 color="$textDarkMuted">
              Search by name, or by what it does.
            </PSText.Body4>
          </YStack>

          <PSInput
            type="text"
            value={query}
            onChangeText={setQuery}
            placeholder="Search pages and components"
          />

          {sections.length > 0 ? (
            sections.map((section) => (
              <YStack key={section.title} gap="$sm">
                <PSText.Body4 color="$textDarkMuted">
                  {section.title}
                </PSText.Body4>
                <YStack>
                  {section.entries.map((entry, i) => (
                    <Fragment key={entry.title}>
                      {i > 0 ? (
                        <View
                          borderBottomWidth="$borderWidth.hairline"
                          borderColor="$borderColor"
                        />
                      ) : null}
                      <CatalogRow
                        entry={entry}
                        onPress={() => router.push(entry.href)}
                      />
                    </Fragment>
                  ))}
                </YStack>
              </YStack>
            ))
          ) : (
            <PSText.Body4 color="$textDarkMuted">
              {`Nothing matches "${query}".`}
            </PSText.Body4>
          )}
        </YStack>
      </ScrollView>
    </>
  );
}

/** A single catalog row — title, blurb, trailing chevron. Same row idiom as
 *  SettingsGroup's rows (`components/SettingsGroup.tsx`): a plain Tamagui
 *  `XStack` with `onPress` + `pressStyle`, not a wrapped `Pressable`, so the
 *  gallery's own navigation reads like the app's existing list rows rather
 *  than inventing a second idiom. Kept local — this row has no reuse outside
 *  this one screen. */
function CatalogRow({
  entry,
  onPress,
}: {
  entry: CatalogEntry;
  onPress: () => void;
}) {
  return (
    <XStack
      items="center"
      gap="$md"
      py="$md"
      cursor="pointer"
      onPress={onPress}
      pressStyle={{ bg: '$borderColor' }}
    >
      <YStack flex={1} gap="$xs">
        <PSText.Body2 color="$textDark">{entry.title}</PSText.Body2>
        <PSText.Body4 color="$textDarkMuted">{entry.blurb}</PSText.Body4>
      </YStack>
      <PSIcon name="chevron-forward" color="$textDarkMuted" size={18} />
    </XStack>
  );
}
